import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import SelectSkeleton from '../SelectSkeleton';
import {
  useLazyGetGoogleDriveFoldersQuery,
  useUpdateGoogleFolderIdMutation,
} from '../../../features/apis/intergration';

interface Folder {
  id: string;
  name: string;
  webViewLink: string;
  createdTime: string;
  modifiedTime: string;
}

const FolderSelect: React.FC = () => {
  const navigate = useNavigate();
  const [updateGoogleFolderId, { isLoading, error }] =
    useUpdateGoogleFolderIdMutation();
  const [getFolders, { data }] = useLazyGetGoogleDriveFoldersQuery();
  const user = useCurrentUser();
  const [folderId, setFolderId] = useState<string>('');

  // Fetch folders on component mount
  useEffect(() => {
    if (user?.companyId) {
      getFolders(user.companyId);
    }
  }, [user?.companyId, getFolders]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFolderId(e.target.value);
  };

  const handleSubmit = async () => {
    if (!folderId) {
      ReactToastify({ success: false, message: 'Please select a folder' });
      return;
    }

    try {
      await updateGoogleFolderId({
        companyId: user?.companyId,
        data: { folderId },
      });

      ReactToastify({
        success: true,
        message: 'Folder info updated successfully',
      });

      navigate('/dashboard/ask-tara');
    } catch (err) {
      console.error('Folder update failed:', err);
      ReactToastify({
        success: false,
        message: 'Something went wrong while saving folder info',
      });
    }
  };

  return (
		<div className="relative w-full h-screen  overflow-hidden font-inter">
			<div className="flex items-center justify-center flex-col h-full">
				<div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
					<h1 className="text-2xl mb-6 font-bold">Select Folder</h1>

					{data?.folders === undefined ? (
						<SelectSkeleton />
					) : (
						<form onSubmit={(e) => e.preventDefault()}>
							<div className="flex flex-col">
								<label
									htmlFor="folderSelect"
									className="mb-2 text-xs font-roboto pl-2"
								>
									Select a folder
								</label>
								<div className="relative ml-2 w-full">
									<select
										id="folderSelect"
										value={folderId}
										onChange={handleSelect}
										className="appearance-none w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] focus:border-transparent text-sm text-gray-700 bg-white"
									>
										<option value="">Select a Folder</option>
										{data.folders.map((folder: Folder) => (
											<option key={folder.id} value={folder.id}>
												{folder.name}
											</option>
										))}
									</select>

									{/* Dropdown Arrow */}
									<div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
										<svg
											className="w-4 h-4 text-[#1B6082]"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
								</div>
							</div>

							<div className="flex justify-end items-center mt-4">
								<button
									type="button"
									onClick={handleSubmit}
									className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center px-4 py-3 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
								</button>
							</div>

							{error && (
								<p className="text-red-500 mt-2">
									Failed to update folder info.
								</p>
							)}
						</form>
					)}
				</div>
			</div>
		</div>
	);
};

export default FolderSelect;
